import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileUp,
  Download,
  ArrowRight,
  Loader2,
  X,
  ShieldCheck,
  Lock,
  FileText,
  RefreshCw,
  Clock,
  CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api, API_BASE, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";

const FIELD_ORDER = [
  "title",
  "author",
  "subject",
  "creator",
  "producer",
  "creation_date",
  "mod_date",
  "keywords",
  "pages",
  "encrypted",
  "has_xmp_metadata",
  "digitally_signed",
];

const CHANGED_KEYS = new Set([
  "creator",
  "producer",
  "creation_date",
  "has_xmp_metadata",
]);

const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
};

const humanSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
};

const MetaRow = ({ label, before, after, changed, index }) => (
  <div
    className="row-anim grid grid-cols-12 border-b border-neutral-900 hover:bg-neutral-900/40"
    style={{ animationDelay: `${index * 30}ms` }}
    data-testid={`meta-row-${label}`}
  >
    <div className="col-span-3 border-r border-neutral-900 px-4 py-3 mono text-[11px] uppercase tracking-[0.22em] text-neutral-500">
      {label}
    </div>
    <div className="col-span-4 border-r border-neutral-900 px-4 py-3 mono text-sm text-neutral-500 truncate">
      {formatValue(before)}
    </div>
    <div className="col-span-1 border-r border-neutral-900 px-2 py-3 flex items-center justify-center">
      <ArrowRight
        size={14}
        className={changed ? "text-[#E2FF54]" : "text-neutral-700"}
      />
    </div>
    <div
      className={cn(
        "col-span-4 px-4 py-3 mono text-sm truncate",
        changed
          ? "bg-[#E2FF54]/[0.06] text-[#E2FF54]"
          : "text-neutral-300"
      )}
    >
      {formatValue(after)}
    </div>
  </div>
);

const Dropzone = ({ file, onFile, onClear, processing }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        "relative border border-dashed p-8 md:p-10 transition-colors",
        dragOver
          ? "border-[#E2FF54] bg-[#E2FF54]/[0.04]"
          : "border-neutral-800 bg-[#0a0a0a] hover:border-neutral-600"
      )}
      data-testid="upload-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
        data-testid="file-input"
      />

      {!file ? (
        <div className="flex flex-col items-start gap-6">
          <div className="flex items-center gap-3">
            <FileUp
              size={22}
              className={dragOver ? "text-[#E2FF54]" : "text-neutral-400"}
            />
            <span className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
              [ input ]
            </span>
          </div>
          <div>
            <p className="mono text-2xl md:text-3xl text-white leading-tight">
              DROP_PDF_HERE
              <span className="blink-cursor text-[#E2FF54]">_</span>
            </p>
            <p className="mono text-xs text-neutral-500 mt-2 tracking-wider">
              or select a file — max 50MB · .pdf only
            </p>
          </div>
          <Button
            onClick={() => inputRef.current?.click()}
            className="btn-brutal rounded-none bg-white text-black hover:bg-neutral-200 mono uppercase tracking-[0.2em] text-xs px-6 py-5"
            data-testid="browse-files-btn"
          >
            BROWSE FILES
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <FileText size={20} className="text-[#E2FF54] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="mono text-sm text-white truncate">{file.name}</p>
                <p className="mono text-[11px] text-neutral-500 mt-1 uppercase tracking-[0.2em]">
                  {humanSize(file.size)} · queued
                </p>
              </div>
            </div>
            {!processing && (
              <button
                onClick={onClear}
                className="text-neutral-500 hover:text-white transition-colors"
                data-testid="clear-file-btn"
                aria-label="Remove file"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PdfMetaRewriter() {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dateMode, setDateMode] = useState("now"); // "now" | "custom"
  const [customDate, setCustomDate] = useState(() => {
    // default to current local datetime formatted for datetime-local input
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  });

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only .pdf files are allowed");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("File too large (max 50MB)");
      return;
    }
    setFile(f);
    setResult(null);
    setError(null);
  };

  const process = useCallback(async () => {
    if (!file) return;
    setProcessing(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (dateMode === "custom" && customDate) {
        // datetime-local is a local-time string like "2020-05-01T09:30"
        // convert to ISO 8601 with Z (UTC) so the backend interprets it consistently
        const localDt = new Date(customDate);
        if (isNaN(localDt.getTime())) {
          toast.error("Invalid date/time");
          setProcessing(false);
          return;
        }
        fd.append("creation_date", localDt.toISOString());
      }
      const { data } = await api.post("/pdf/rewrite", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("Metadata rewritten · downloading now");
      const link = document.createElement("a");
      link.href = `${API_BASE}/pdf/download/${data.file_id}?name=${encodeURIComponent(
        data.download_name
      )}`;
      link.download = data.download_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }, [file, dateMode, customDate]);

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  const rows = useMemo(() => {
    const before = result?.original_metadata || {};
    const after = result?.modified_metadata || {};
    return FIELD_ORDER.map((k) => ({
      key: k,
      before: before[k],
      after: after[k],
      changed: result ? CHANGED_KEYS.has(k) : false,
    }));
  }, [result]);

  return (
    <AppShell>
      <section className="px-6 md:px-12 pt-12 md:pt-16 pb-8 md:pb-12 border-b border-neutral-900">
        <div className="max-w-6xl">
          <p className="mono text-[11px] uppercase tracking-[0.35em] text-[#E2FF54] mb-6">
            // pdf metadata rewriter
          </p>
          <h1 className="display text-5xl md:text-7xl lg:text-8xl font-bold uppercase text-white leading-[0.92]">
            Stamp any PDF
            <br />
            as <span className="text-[#E2FF54]">Apache FOP</span>
            <br />
            <span className="text-neutral-500">Version 2.3</span>
          </h1>
          <p className="mono text-sm md:text-base text-neutral-400 mt-8 max-w-2xl leading-relaxed">
            Overwrites the producer & creator to
            <span className="text-white"> "Apache FOP Version 2.3"</span>,
            refreshes the creation timestamp, clears leftover author/title/subject/keywords, and
            embeds a matching XMP metadata packet. Every rewrite is logged to
            your portal.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 border-b border-neutral-900">
        <div className="lg:col-span-5 lg:border-r border-neutral-900 p-6 md:p-10 space-y-6">
          <div className="flex items-baseline justify-between">
            <h2 className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
              [ step_01 ] upload
            </h2>
            {file && (
              <button
                onClick={reset}
                className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white flex items-center gap-1.5"
                data-testid="reset-btn"
              >
                <RefreshCw size={11} /> reset
              </button>
            )}
          </div>

          <Dropzone
            file={file}
            onFile={handleFile}
            onClear={reset}
            processing={processing}
          />

          {/* Date option */}
          <div className="border border-neutral-900 p-4 space-y-3" data-testid="date-option-panel">
            <div className="flex items-center gap-2">
              <CalendarClock size={13} className="text-[#E2FF54]" />
              <p className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                creation date
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDateMode("now")}
                data-testid="date-mode-now"
                className={cn(
                  "mono text-[11px] uppercase tracking-[0.2em] px-3 py-2.5 border transition-colors flex items-center justify-center gap-2",
                  dateMode === "now"
                    ? "border-[#E2FF54] bg-[#E2FF54]/[0.08] text-[#E2FF54]"
                    : "border-neutral-800 text-neutral-500 hover:text-white hover:border-neutral-600"
                )}
              >
                <Clock size={12} /> use current time
              </button>
              <button
                type="button"
                onClick={() => setDateMode("custom")}
                data-testid="date-mode-custom"
                className={cn(
                  "mono text-[11px] uppercase tracking-[0.2em] px-3 py-2.5 border transition-colors flex items-center justify-center gap-2",
                  dateMode === "custom"
                    ? "border-[#E2FF54] bg-[#E2FF54]/[0.08] text-[#E2FF54]"
                    : "border-neutral-800 text-neutral-500 hover:text-white hover:border-neutral-600"
                )}
              >
                <CalendarClock size={12} /> pick a date
              </button>
            </div>
            {dateMode === "custom" && (
              <div className="pt-2">
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-neutral-600 block mb-1.5">
                  any past or future date & time
                </label>
                <input
                  type="datetime-local"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  data-testid="custom-date-input"
                  className="w-full rounded-none bg-[#0a0a0a] border border-neutral-800 focus:border-[#E2FF54] focus:outline-none mono text-sm text-white px-3 py-2.5"
                  style={{ colorScheme: "dark" }}
                />
                <p className="mono text-[10px] text-neutral-600 mt-2 tracking-wider">
                  applies to CreationDate + xmp:CreateDate/ModifyDate/MetadataDate + dc:date
                </p>
              </div>
            )}
          </div>

          <div className="pt-2">
            <Button
              disabled={!file || processing}
              onClick={process}
              className={cn(
                "btn-brutal w-full rounded-none mono uppercase tracking-[0.24em] text-xs py-7",
                file && !processing
                  ? "bg-[#E2FF54] text-black hover:bg-[#d4f531]"
                  : "bg-neutral-900 text-neutral-600 hover:bg-neutral-900 cursor-not-allowed"
              )}
              data-testid="process-btn"
            >
              {processing ? (
                <span className="flex items-center gap-3">
                  <Loader2 size={16} className="animate-spin" />
                  REWRITING_XMP_DATA...
                </span>
              ) : (
                <span className="flex items-center gap-3">
                  <ShieldCheck size={16} />
                  REWRITE & DOWNLOAD
                </span>
              )}
            </Button>
            {processing && (
              <div className="mt-4 h-1 w-full bg-neutral-900 overflow-hidden relative">
                <div className="progress-shine absolute inset-y-0 w-1/4 bg-[#E2FF54]" />
              </div>
            )}
          </div>

          {error && (
            <div
              className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-4 mono text-xs text-[#FF9994]"
              data-testid="error-box"
            >
              <div className="uppercase tracking-[0.22em] text-[10px] text-[#FF3B30] mb-1">
                error
              </div>
              {error}
            </div>
          )}

          {result && (
            <div className="border border-[#E2FF54]/30 bg-[#E2FF54]/[0.04] p-5 flex items-start gap-3">
              <ShieldCheck size={18} className="text-[#E2FF54] mt-0.5" />
              <div className="min-w-0">
                <p className="mono text-xs uppercase tracking-[0.22em] text-[#E2FF54]">
                  rewrite_complete
                </p>
                <p className="mono text-sm text-white mt-1 truncate">
                  {result.download_name}
                </p>
                <a
                  href={`${API_BASE}/pdf/download/${result.file_id}?name=${encodeURIComponent(
                    result.download_name
                  )}`}
                  download={result.download_name}
                  className="mt-3 inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.22em] text-black bg-white hover:bg-neutral-200 px-4 py-2 btn-brutal"
                  data-testid="download-again-btn"
                >
                  <Download size={14} /> download again
                </a>
              </div>
            </div>
          )}

          <div className="pt-6 border-t border-neutral-900 space-y-2">
            <p className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-600">
              what changes
            </p>
            <ul className="mono text-xs text-neutral-400 space-y-1.5 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-[#E2FF54]">›</span> creator ← Apache FOP Version 2.3
              </li>
              <li className="flex gap-2">
                <span className="text-[#E2FF54]">›</span> producer ← Apache FOP Version 2.3
              </li>
              <li className="flex gap-2">
                <span className="text-[#E2FF54]">›</span> creation_date ← {dateMode === "now" ? "now (UTC)" : "your chosen date"}
              </li>
              <li className="flex gap-2">
                <span className="text-[#E2FF54]">›</span> xmp packet ← rebuilt
              </li>
              <li className="flex gap-2">
                <span className="text-[#E2FF54]">›</span> file size ← ~37.5 KB (FOP-style)
              </li>
              <li className="flex gap-2">
                <span className="text-neutral-600">›</span> title, author, subject, keywords cleared
              </li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7 p-6 md:p-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-500">
              [ step_02 ] properties
            </h2>
            <span className="mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
              before → after
            </span>
          </div>

          <div className="border border-neutral-900" data-testid="metadata-table">
            <div className="grid grid-cols-12 border-b border-neutral-800 bg-[#0a0a0a]">
              <div className="col-span-3 border-r border-neutral-900 px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
                field
              </div>
              <div className="col-span-4 border-r border-neutral-900 px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
                original
              </div>
              <div className="col-span-1 border-r border-neutral-900 px-2 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600 text-center">
                →
              </div>
              <div className="col-span-4 px-4 py-3 mono text-[10px] uppercase tracking-[0.22em] text-neutral-600">
                new
              </div>
            </div>

            {rows.map((r, idx) => (
              <MetaRow
                key={r.key}
                label={r.key}
                before={r.before}
                after={result ? r.after : null}
                changed={r.changed}
                index={idx}
              />
            ))}
          </div>

          {!result && (
            <div className="mt-6 mono text-[11px] text-neutral-600 uppercase tracking-[0.22em] flex items-center gap-2">
              <Lock size={12} /> awaiting file to display live diff
            </div>
          )}
        </div>
      </section>

      <footer className="px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
        <p className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-600">
          fop.rewrite · pikepdf backend · every rewrite is logged
        </p>
        <p className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-700">
          built with pikepdf · react · tailwind
        </p>
      </footer>
    </AppShell>
  );
}
